// src/components/CompetitiveAdvantageSection.js

import React from 'react';
import styled from 'styled-components';
import { FaTrophy, FaUsers, FaChartLine, FaShieldAlt, FaRocket } from 'react-icons/fa';
import { Section } from '../common/Sections';

const CompetitiveAdvantageSection = () => {
  // Player-specific advantages
  const playerAdvantages = [
    {
      icon: <FaTrophy />,
      title: 'Competitive Advantage',
      description: "Enhance your game with instant access to high-definition footage. Whether you're striving for precision or mastering technique, our platform helps you spot opportunities for improvement and develop strategies to outplay your competition."
    },
    {
      icon: <FaUsers />,
      title: 'Relive the Moment',
      description: "Never miss a moment. Go back and relive those game-changing plays, clutch shots, and defining victories anytime, anywhere. Our platform gives you seamless access to every highlight so your memories stay fresh and your skills continue to sharpen."
    },
    {
      icon: <FaChartLine />,
      title: 'Share',
      description: "Celebrate with your community. Showcase your standout moments with teammates, coaches, and friends in just a few clicks—whether it's for bragging rights or building your personal portfolio."
    },
    {
      icon: <FaShieldAlt />,
      title: 'Easy to Use',
      description: "Accessing your footage is as simple as scanning our QR code after your game, selecting the time and court you played on, purchasing your video, and then accessing it via your Akture Dashboard."
    }
  ];

  return (
    <Section id="competitive-advantage">
      <Container>
        <ContentWrapper>
          <TextContent>
            <SectionLabel>For Athletes</SectionLabel>
            <Title>For Players</Title>
            {/* <Description>
              Our platform provides key benefits specifically tailored for athletes—empowering you to elevate your game, relive your finest moments, and share your success with ease.
            </Description> */}
          </TextContent>

          <AdvantageGrid>
            {playerAdvantages.map((item, index) => (
              <AdvantageCard key={index}>
                <CardIcon>{item.icon}</CardIcon>
                <CardTitle>{item.title}</CardTitle>
                <CardDescription>{item.description}</CardDescription>
              </AdvantageCard>
            ))}
          </AdvantageGrid>
        </ContentWrapper>
      </Container>
    </Section>
  );
};

export default CompetitiveAdvantageSection;

/* ────────────── STYLED COMPONENTS ────────────── */

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.xl};

  @media (max-width: 768px) {
    padding: 0 ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: 480px) {
    padding: 0 ${({ theme }) => theme.spacing.md};
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxl};

  @media (max-width: 768px) {
    gap: ${({ theme }) => theme.spacing.xl};
  }
`;

const TextContent = styled.div`
  max-width: 520px;
  margin: 0 auto;
  text-align: center;
`;

const SectionLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: ${({ theme }) => theme.colors.accentPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-weight: 600;
  opacity: 0.9;
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.02em;
  position: relative;

  &::after {
    content: '';
    display: block;
    width: 40px;
    height: 2px;
    background: ${({ theme }) => theme.colors.accentPrimary};
    margin: ${({ theme }) => theme.spacing.sm} auto 0;
    border-radius: ${({ theme }) => theme.borderRadius.full};
  }

  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.fontSizes['2xl']};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: 480px) {
    font-size: ${({ theme }) => theme.fontSizes.xl};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  line-height: 1.7;
  letter-spacing: -0.01em;

  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.fontSizes.lg};
    line-height: 1.6;
  }

  @media (max-width: 480px) {
    font-size: ${({ theme }) => theme.fontSizes.md};
    line-height: 1.5;
  }
`;

const AdvantageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const AdvantageCard = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-4px);
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const CardIcon = styled.div`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.accentPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
`;

const CardDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.fontSizes.sm};
  }
`;
